package screens.notification

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.presentation.notificationsettings.NotificationSettingsEvent
import com.example.readingnotes.presentation.notificationsettings.NotificationSettingsViewModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import styles.borderBottom
import styles.boxShadow
import styles.property
import styles.transition

@Composable
fun NotificationSettingsScreen(
    onBackClick: () -> Unit,
    appModule: AppModule,
    appScope: CoroutineScope,
) {
    val viewModel = remember {
        NotificationSettingsViewModel(
            appModule.getNotificationSettingsUseCase,
            appModule.updateNotificationSettingsUseCase,
            appScope,
        )
    }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.onEvent(NotificationSettingsEvent.OnLoadSettings)
    }

    Div({ classes(AppStyleSheet.container) }) {
        // Header
        Div({
            classes(AppStyleSheet.header)
            style {
                paddingBottom(16.px)
            }
        }) {
            Button({
                classes(AppStyleSheet.secondaryButton)
                onClick { onBackClick() }
                style { marginRight(16.px) }
            }) {
                Text("← 戻る")
            }
            H1 { Text("通知設定") }
        }

        // Content
        val settingsValue = state.settings
        when {
            state.isLoading -> Div({ classes(AppStyleSheet.loading) }) { Text("読み込み中...") }
            state.error != null -> {
                val errorMessage = state.error ?: ""
                Div({ classes(AppStyleSheet.error) }) {
                    Text(errorMessage)
                    Button({
                        classes(AppStyleSheet.primaryButton)
                        style { marginTop(16.px) }
                        onClick { viewModel.onEvent(NotificationSettingsEvent.OnLoadSettings) }
                    }) { Text("リトライ") }
                }
            }
            settingsValue != null -> {
                NotificationSettingsForm(
                    settings = settingsValue,
                    isSaving = state.isSaving,
                    error = state.error,
                    saveSuccess = state.saveSuccess,
                    onUpdateSettings = { settings ->
                        viewModel.onEvent(NotificationSettingsEvent.OnUpdateSettings(settings))
                    },
                )
            }
        }
    }
}

@Composable
private fun NotificationSettingsForm(
    settings: com.example.readingnotes.domain.model.NotificationSettings,
    isSaving: Boolean,
    error: String?,
    saveSuccess: Boolean?,
    onUpdateSettings: (com.example.readingnotes.domain.model.NotificationSettings) -> Unit,
) {
    var localSettings by remember { mutableStateOf(settings) }

    LaunchedEffect(settings) {
        localSettings = settings
    }

    LaunchedEffect(saveSuccess) {
        if (saveSuccess == true) {
            // Clear success message after delay
            kotlinx.coroutines.delay(3000)
        }
    }

    Div({
        style {
            maxWidth(600.px)
            marginLeft(0.px)
            marginRight(0.px)
        }
    }) {
        // Success message
        if (saveSuccess == true) {
            Div({
                style {
                    backgroundColor(Color("#4CAF50"))
                    color(Color.white)
                    padding(12.px, 16.px)
                    borderRadius(8.px)
                    marginBottom(16.px)
                    textAlign("center")
                }
            }) {
                Text("設定を保存しました")
            }
        }

        // Notification type toggles
        Div({
            style {
                backgroundColor(Color.white)
                padding(24.px)
                borderRadius(8.px)
                marginBottom(16.px)
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            }
        }) {
            H3({ style { marginBottom(16.px) } }) { Text("通知種別") }

            ToggleRow(
                label = "新着投稿",
                checked = localSettings.enableNewPost,
                onCheckedChange = { localSettings = localSettings.copy(enableNewPost = it) }
            )

            ToggleRow(
                label = "新着コメント",
                checked = localSettings.enableNewComment,
                onCheckedChange = { localSettings = localSettings.copy(enableNewComment = it) }
            )

            ToggleRow(
                label = "コメント返信",
                checked = localSettings.enableCommentReply,
                onCheckedChange = { localSettings = localSettings.copy(enableCommentReply = it) }
            )

            ToggleRow(
                label = "メンション",
                checked = localSettings.enableMention,
                onCheckedChange = { localSettings = localSettings.copy(enableMention = it) }
            )

            ToggleRow(
                label = "システム通知",
                checked = localSettings.enableSystem,
                onCheckedChange = { localSettings = localSettings.copy(enableSystem = it) }
            )
        }

        // Mute time settings
        Div({
            style {
                backgroundColor(Color.white)
                padding(24.px)
                borderRadius(8.px)
                marginBottom(16.px)
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            }
        }) {
            H3({ style { marginBottom(16.px) } }) { Text("ミュート時間帯") }

            P({
                style {
                    fontSize(14.px)
                    color(Color("#666"))
                    marginBottom(16.px)
                }
            }) {
                Text("設定した時間帯は通知をミュートします（例: 22:00 - 7:00）")
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(16.px)
                }
            }) {
                Div({ style { flex(1) } }) {
                    Label(attrs = {
                        style {
                            display(DisplayStyle.Block)
                            marginBottom(8.px)
                            property("font-weight", "bold")
                        }
                    }) { Text("開始時刻") }
                    Input(type = InputType.Time) {
                        value(localSettings.muteStart ?: "")
                        onInput { localSettings = localSettings.copy(muteStart = it.value) }
                        style {
                            width(100.percent)
                            padding(8.px)
                            borderRadius(4.px)
                            border(1.px, LineStyle.Solid, Color("#ddd"))
                        }
                    }
                }

                Div({ style { flex(1) } }) {
                    Label(attrs = {
                        style {
                            display(DisplayStyle.Block)
                            marginBottom(8.px)
                            property("font-weight", "bold")
                        }
                    }) { Text("終了時刻") }
                    Input(type = InputType.Time) {
                        value(localSettings.muteEnd ?: "")
                        onInput { localSettings = localSettings.copy(muteEnd = it.value) }
                        style {
                            width(100.percent)
                            padding(8.px)
                            borderRadius(4.px)
                            border(1.px, LineStyle.Solid, Color("#ddd"))
                        }
                    }
                }
            }
        }

        // Error message
        if (error != null) {
            Div({
                classes(AppStyleSheet.error)
                style { marginBottom(16.px) }
            }) {
                Text(error)
            }
        }

        // Save button
        Button({
            classes(AppStyleSheet.primaryButton)
            onClick { onUpdateSettings(localSettings) }
            if (isSaving) attr("disabled", "true")
            style {
                width(100.percent)
                if (isSaving) {
                    opacity(0.6)
                    cursor("not-allowed")
                }
            }
        }) {
            Text(if (isSaving) "保存中..." else "設定を保存")
        }
    }
}

@Composable
private fun ToggleRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(12.px, 0.px)
            property("border-bottom", "1px solid ${Color("#eee")}")
        }
    }) {
        Label(attrs = {
            style {
                flexGrow(1)
                cursor("pointer")
            }
            attr("for", "toggle-$label")
        }) { Text(label) }
        ToggleSwitch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            label = label
        )
    }
}

@Composable
private fun ToggleSwitch(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    label: String,
) {
    val toggleId = "toggle-$label-${label.hashCode()}"
    Button({
        id(toggleId)
        attr("role", "switch")
        attr("aria-checked", checked.toString())
        attr("aria-label", "$label: ${if (checked) "オン" else "オフ"}")
        style {
            width(48.px)
            height(28.px)
            borderRadius(14.px)
            border(0.px)
            cursor("pointer")
            backgroundColor(if (checked) Color("#4CAF50") else Color("#ccc"))
            property("transition", "background-color 0.2s")
            position(Position.Relative)
        }
        onClick { onCheckedChange(!checked) }
    }) {
        Span({
            style {
                position(Position.Absolute)
                top(2.px)
                left(if (checked) 26.px else 2.px)
                width(24.px)
                height(24.px)
                borderRadius(50.percent)
                backgroundColor(Color.white)
                property("transition", "left 0.2s")
                property("box-shadow", "0 1px 3px rgba(0,0,0,0.3)")
            }
            attr("aria-hidden", "true")
        })
    }
}
